import { useState, useRef } from 'react';
import { 
  FileDown, 
  Copy, 
  Printer, 
  X, 
  Check,
  Calendar,
  MapPin,
  User,
  FileText,
  Image as ImageIcon,
  Mail,
  Building2
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../../lib/utils';

// Application Preview Component - Renders the application in a clean format
const ApplicationPreview = ({ application, user, forPrint = false }) => {
  const event = application.event || {};
  const profile = application.profile || {};
  const images = application.images || [];
  
  return (
    <div 
      className={`bg-white ${forPrint ? '' : 'max-h-[60vh] overflow-y-auto'}`}
      style={{ fontFamily: 'Arial, sans-serif' }}
    >
      {/* Header */}
      <div className="bg-[#1E3A5F] text-white p-6 rounded-t-lg print:rounded-none">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Event Application</h1>
            <p className="text-white/80 text-sm">AMSCompass</p>
          </div>
        </div>
      </div>
      
      {/* Event Info */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-bold text-[#1E3A5F] mb-3">{event.title || 'Event'}</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#FE5F55]" />
            <span>
              {event.date ? formatDate(event.date) : 'TBD'}
              {event.end_date && event.end_date !== event.date && ` - ${formatDate(event.end_date)}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#FE5F55]" />
            <span>{event.city}{event.state ? `, ${event.state}` : ''}</span>
          </div>
          {event.booth_cost > 0 && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#FE5F55]" />
              <span>Booth Fee: {formatCurrency(event.booth_cost)}</span>
            </div>
          )}
          {event.event_type && (
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#FE5F55]" />
              <span className="capitalize">{event.event_type.replace('_', ' ')}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Applicant Info */}
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-md font-bold text-[#1E3A5F] mb-3 flex items-center gap-2">
          <User className="w-4 h-4" />
          Applicant Information
        </h3>
        <div className="space-y-2 text-sm">
          <p><strong>Name/Business:</strong> {user?.business_name || user?.name || 'N/A'}</p>
          <p><strong>Email:</strong> {user?.email || 'N/A'}</p>
          {user?.craft_type && <p><strong>Craft Type:</strong> {user.craft_type}</p>}
          {user?.phone && <p><strong>Phone:</strong> {user.phone}</p>}
          {user?.website && <p><strong>Website:</strong> {user.website}</p>}
        </div>
      </div>
      
      {/* Profile Info */}
      {profile && profile.name && (
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-md font-bold text-[#1E3A5F] mb-3">
            Artist Profile: {profile.name}
          </h3>
          <div className="space-y-4 text-sm">
            {profile.artist_statement && (
              <div>
                <p className="font-semibold text-gray-700 mb-1">Artist Statement</p>
                <p className="text-gray-600 whitespace-pre-wrap">{profile.artist_statement}</p>
              </div>
            )}
            {profile.product_description && (
              <div>
                <p className="font-semibold text-gray-700 mb-1">Product Description</p>
                <p className="text-gray-600 whitespace-pre-wrap">{profile.product_description}</p>
              </div>
            )}
            {profile.booth_description && (
              <div>
                <p className="font-semibold text-gray-700 mb-1">Booth Description</p>
                <p className="text-gray-600 whitespace-pre-wrap">{profile.booth_description}</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Application Notes */}
      {application.notes && (
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-md font-bold text-[#1E3A5F] mb-3">Application Notes</h3>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{application.notes}</p>
        </div>
      )}
      
      {/* Images */}
      {images.length > 0 && (
        <div className="p-6">
          <h3 className="text-md font-bold text-[#1E3A5F] mb-3 flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Attached Images ({images.length})
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {images.map((img) => (
              <div key={img.id} className="text-center">
                <img
                  src={img.url}
                  alt={img.name || 'Application image'}
                  className="w-full h-40 object-cover rounded-lg border border-gray-200"
                />
                <p className="text-xs text-gray-500 mt-1">{img.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Footer */}
      <div className="p-4 bg-gray-50 text-center text-xs text-gray-500 rounded-b-lg print:rounded-none">
        Generated by AMSCompass • {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

// Generate plain text version for clipboard
const generatePlainText = (application, user) => {
  const event = application.event || {};
  const profile = application.profile || {};
  
  let text = `EVENT APPLICATION\n`;
  text += `${'='.repeat(50)}\n\n`;
  
  // Event Info
  text += `EVENT: ${event.title || 'N/A'}\n`;
  text += `Date: ${event.date ? formatDate(event.date) : 'TBD'}`;
  if (event.end_date && event.end_date !== event.date) {
    text += ` - ${formatDate(event.end_date)}`;
  }
  text += `\n`;
  text += `Location: ${event.city || ''}${event.state ? `, ${event.state}` : ''}\n`;
  if (event.booth_cost > 0) {
    text += `Booth Fee: ${formatCurrency(event.booth_cost)}\n`;
  }
  text += `\n`;
  
  // Applicant Info
  text += `APPLICANT INFORMATION\n`;
  text += `${'-'.repeat(30)}\n`;
  text += `Name/Business: ${user?.business_name || user?.name || 'N/A'}\n`;
  text += `Email: ${user?.email || 'N/A'}\n`;
  if (user?.craft_type) text += `Craft Type: ${user.craft_type}\n`;
  if (user?.phone) text += `Phone: ${user.phone}\n`;
  if (user?.website) text += `Website: ${user.website}\n`;
  text += `\n`;
  
  // Profile
  if (profile && profile.name) {
    text += `ARTIST PROFILE: ${profile.name}\n`;
    text += `${'-'.repeat(30)}\n`;
    
    if (profile.artist_statement) {
      text += `\nArtist Statement:\n${profile.artist_statement}\n`;
    }
    if (profile.product_description) {
      text += `\nProduct Description:\n${profile.product_description}\n`;
    }
    if (profile.booth_description) {
      text += `\nBooth Description:\n${profile.booth_description}\n`;
    }
    text += `\n`;
  }
  
  // Notes
  if (application.notes) {
    text += `APPLICATION NOTES\n`;
    text += `${'-'.repeat(30)}\n`;
    text += `${application.notes}\n\n`;
  }
  
  // Images note
  const images = application.images || [];
  if (images.length > 0) {
    text += `ATTACHED IMAGES: ${images.length} image(s)\n`;
    images.forEach((img, i) => {
      text += `  ${i + 1}. ${img.name || 'Image'}: ${img.url}\n`;
    });
  }
  
  text += `\n${'='.repeat(50)}\n`;
  text += `Generated by AMSCompass • ${new Date().toLocaleDateString()}\n`;
  
  return text;
};

// Main Export Dialog Component
export const ApplicationExportDialog = ({ 
  isOpen, 
  onClose, 
  application, 
  user 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const previewRef = useRef(null);
  
  if (!application) return null;
  
  // Save as PDF
  const handleSavePDF = async () => {
    setIsGenerating(true);
    try {
      // Dynamically import html2pdf to avoid SSR issues
      const html2pdf = (await import('html2pdf.js')).default;
      
      const element = previewRef.current;
      const eventTitle = application.event?.title || 'Application';
      const fileName = `Application_${eventTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      
      const opt = {
        margin: 10,
        filename: fileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      await html2pdf().set(opt).from(element).save();
      toast.success('PDF saved successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Copy to Clipboard
  const handleCopyToClipboard = async () => {
    try {
      const text = generatePlainText(application, user);
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy to clipboard');
    }
  };
  
  // Print
  const handlePrint = () => {
    const printContent = previewRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Application - ${application.event?.title || 'Event'}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; padding: 20px; }
            .bg-\\[\\#1E3A5F\\] { background-color: #1E3A5F; }
            .text-white { color: white; }
            .text-\\[\\#1E3A5F\\] { color: #1E3A5F; }
            .text-\\[\\#FE5F55\\] { color: #FE5F55; }
            .text-gray-600 { color: #4B5563; }
            .text-gray-500 { color: #6B7280; }
            .text-gray-700 { color: #374151; }
            .font-bold { font-weight: bold; }
            .font-semibold { font-weight: 600; }
            .text-xl { font-size: 1.25rem; }
            .text-lg { font-size: 1.125rem; }
            .text-md { font-size: 1rem; }
            .text-sm { font-size: 0.875rem; }
            .text-xs { font-size: 0.75rem; }
            .p-6 { padding: 1.5rem; }
            .p-4 { padding: 1rem; }
            .mb-1 { margin-bottom: 0.25rem; }
            .mb-2 { margin-bottom: 0.5rem; }
            .mb-3 { margin-bottom: 0.75rem; }
            .mt-1 { margin-top: 0.25rem; }
            .gap-2 { gap: 0.5rem; }
            .gap-3 { gap: 0.75rem; }
            .gap-4 { gap: 1rem; }
            .space-y-2 > * + * { margin-top: 0.5rem; }
            .space-y-4 > * + * { margin-top: 1rem; }
            .border-b { border-bottom: 1px solid #E5E7EB; }
            .grid { display: grid; }
            .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
            .flex { display: flex; }
            .items-center { align-items: center; }
            .justify-center { justify-content: center; }
            .rounded-lg { border-radius: 0.5rem; }
            .bg-gray-50 { background-color: #F9FAFB; }
            .bg-white\\/20 { background-color: rgba(255,255,255,0.2); }
            .text-white\\/80 { color: rgba(255,255,255,0.8); }
            .text-center { text-align: center; }
            .whitespace-pre-wrap { white-space: pre-wrap; }
            .object-cover { object-fit: cover; }
            .w-full { width: 100%; }
            .h-40 { height: 10rem; }
            .w-10 { width: 2.5rem; }
            .h-10 { height: 2.5rem; }
            .w-4 { width: 1rem; }
            .h-4 { height: 1rem; }
            .w-6 { width: 1.5rem; }
            .h-6 { height: 1.5rem; }
            img { max-width: 100%; page-break-inside: avoid; }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for images to load
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1E3A5F] flex items-center gap-2">
            <FileDown className="w-5 h-5" />
            Export Application
          </DialogTitle>
        </DialogHeader>
        
        {/* Export Buttons */}
        <div className="flex gap-3 py-3 border-b border-gray-200" data-testid="export-dialog-buttons">
          <Button
            onClick={handleSavePDF}
            disabled={isGenerating}
            className="flex-1 bg-[#1E3A5F] hover:bg-[#152a47] text-white"
            data-testid="export-pdf-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {isGenerating ? 'Generating...' : 'Save as PDF'}
          </Button>
          
          <Button
            onClick={handleCopyToClipboard}
            variant="outline"
            className="flex-1 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#FFEDDF]"
            data-testid="export-clipboard-btn"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copy to Clipboard
              </>
            )}
          </Button>
          
          <Button
            onClick={handlePrint}
            variant="outline"
            className="flex-1 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#FFEDDF]"
            data-testid="export-print-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
        
        {/* Preview */}
        <div className="flex-1 overflow-hidden" data-testid="export-preview-section">
          <p className="text-xs text-gray-500 mb-2">Preview:</p>
          <div 
            ref={previewRef} 
            className="border border-gray-200 rounded-lg overflow-hidden"
            data-testid="export-preview-content"
          >
            <ApplicationPreview 
              application={application} 
              user={user} 
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationExportDialog;
